import React, { useState, useRef } from 'react';
import { getPaginationModel } from './paginationUtils';
import AdditionalItemGroupTooltip from './AdditionalItemGroupTooltip';

const AdditionalItemGroupManager = ({ groups, onEdit, onDelete, searchTerm, onSearchChange, page, onPageChange, onSelect, selectedGroupIds, unselectAll, searchInputRef }) => {
  const [tooltip, setTooltip] = useState({ visible: false, group: null, target: null, triggeredBy: null });
  const tooltipTimer = useRef(null);

  const handleMouseEnter = (e, group) => {
    if (window.innerWidth < 768) return;
    const target = e.currentTarget;
    if (tooltipTimer.current) {
      clearTimeout(tooltipTimer.current);
    }
    tooltipTimer.current = setTimeout(() => {
      setTooltip({ visible: true, group, target, triggeredBy: 'hover' });
    }, 500);
  };

  const handleMouseLeave = () => {
    if (tooltipTimer.current) {
      clearTimeout(tooltipTimer.current);
    }
    if (tooltip.triggeredBy === 'hover') {
      setTooltip({ visible: false, group: null, target: null, triggeredBy: null });
    }
  };

  const sortedGroups = [...groups].sort((a, b) => a.title.localeCompare(b.title));
  const pageSize = 5;
  const totalPages = Math.ceil(sortedGroups.length / pageSize);
  const paginatedGroups = sortedGroups.slice((page - 1) * pageSize, page * pageSize);
  const paginationModel = getPaginationModel(page, totalPages);

  const emptyRows = pageSize - paginatedGroups.length;

  return (
    <div>
      {groups.length === 0 && searchTerm.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '1rem' }}>No item groups found.<br />Add one by using the + button.</div>
      ) : (
        <>
          <div className="recipe-filter-controls" style={{ display: 'flex', alignItems: 'center' }}>
            {selectedGroupIds.length > 0 && <button title="Unselect all groups" onClick={unselectAll} className="unselect-button">Unselect</button>}
            <div className={`search-input-container ${selectedGroupIds.length > 0 ? 'collapsed' : 'expanded'}`}>
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search Groups"
                value={searchTerm}
                onChange={e => onSearchChange(e.target.value)}
              />
              {searchTerm && <button onClick={() => { onSearchChange(''); searchInputRef.current.focus(); }} className="clear-input-button" title="Clear search"><i className="material-icons">close</i></button>}
            </div>
          </div>
        </>
      )}
      {paginatedGroups.length === 0 && searchTerm.length > 0 ? (
        <div style={{ textAlign: 'center', padding: '1rem' }}>No groups found for "{searchTerm}".</div>
      ) : (
        <>
          <ul className="cookbook-list">
            {paginatedGroups.map((group) => (
              <li
                key={group.id}
                onClick={() => onSelect(group.id)}
                className={selectedGroupIds.includes(group.id) ? 'selected' : ''}
                onMouseEnter={(e) => handleMouseEnter(e, group)}
                onMouseLeave={handleMouseLeave}
              >
                <div className="recipe-name" style={{ display: 'flex', alignItems: 'center' }}>
                  <button
                    className="icon-button mobile-view-button"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (tooltip.visible && tooltip.group && tooltip.group.id === group.id && tooltip.triggeredBy === 'click') {
                        setTooltip({ visible: false, group: null, target: null, triggeredBy: null });
                      } else {
                        setTooltip({ visible: true, group, target: e.currentTarget, triggeredBy: 'click' });
                      }
                    }}
                    title="View details"
                    style={{ marginRight: '5px' }}
                  >
                    <i className="material-icons">visibility</i>
                  </button>
                  {group.title}
                </div>
                <div className="item-actions">
                  <button onClick={(e) => { e.stopPropagation(); onEdit(group); }} className="icon-button" title="Edit group"><i className="material-icons">edit</i></button>
                  <button onClick={(e) => { e.stopPropagation(); onDelete(group.id); }} className="icon-button delete-button" title="Delete group"><i className="material-icons">delete</i></button>
                </div>
              </li>
            ))}
            {page === totalPages && totalPages > 1 && emptyRows > 0 && [...Array(emptyRows)].map((_, index) => (
              <li key={`empty-${index}`} style={{ visibility: 'hidden' }}>
                &nbsp;
                <div className="item-actions">
                  <button className="icon-button" title="Edit group"><i className="material-icons">edit</i></button>
                  <button className="icon-button delete-button" title="Delete group"><i className="material-icons">delete</i></button>
                </div>
              </li>
            ))}
          </ul>
          {tooltip.visible && (
            <AdditionalItemGroupTooltip
              group={tooltip.group}
              target={tooltip.target}
              mobileMode={tooltip.triggeredBy === 'click'}
              onClose={() => setTooltip({ visible: false, group: null, target: null, triggeredBy: null })}
            />
          )}
          {totalPages > 1 && (
            <div className="pagination-controls">
              <button onClick={() => onPageChange(page - 1)} disabled={page === 1} className="icon-button" title="Previous page"><i className="material-icons">keyboard_arrow_left</i></button>
              <div className="page-numbers">
                {paginationModel.map((item, index) => (
                  <button key={index} onClick={() => onPageChange(item)} disabled={item === '...' || item === page} className={item === page ? 'active' : ''} title={`Go to page ${item}`}>
                    {item}
                  </button>
                ))}
              </div>
              <button onClick={() => onPageChange(page + 1)} disabled={paginatedGroups.length < pageSize || page * pageSize >= groups.length} className="icon-button" title="Next page"><i className="material-icons">keyboard_arrow_right</i></button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AdditionalItemGroupManager;